/**
 * The regional feed keys its cells by ISO week label (`2026-W09`); the distribution feed
 * keys its by the week's Monday (`2026-02-23`). One week vocabulary reaches the domain --
 * the Monday -- so a fact from either family is comparable without a conversion at the
 * call site. This module is that conversion, and the label form stops here.
 */

const ISO_WEEK_LABEL = /^(\d{4})-W(\d{2})$/;
const DAY_MS = 86_400_000;
/** ISO 8601: week 1 is the week containing January 4th. */
const WEEK_1_ANCHOR_DAY = 4;
const DAYS_PER_WEEK = 7;

/** Day of week with Monday=1 and Sunday=7, rather than JS's Sunday=0. */
function isoDayOfWeek(epochMs: number): number {
  return new Date(epochMs).getUTCDay() || DAYS_PER_WEEK;
}

/** Monday of the given ISO week, without asserting that the week exists in that year. */
function mondayOfIsoWeek(year: number, week: number): number {
  const anchor = Date.UTC(year, 0, WEEK_1_ANCHOR_DAY);
  const week1Monday = anchor - (isoDayOfWeek(anchor) - 1) * DAY_MS;
  return week1Monday + (week - 1) * DAYS_PER_WEEK * DAY_MS;
}

/** The ISO week-year and week number a date falls in, identified by its Thursday. */
function isoWeekOf(epochMs: number): { readonly year: number; readonly week: number } {
  const thursday = epochMs + (4 - isoDayOfWeek(epochMs)) * DAY_MS;
  const year = new Date(thursday).getUTCFullYear();
  const week = Math.floor((thursday - Date.UTC(year, 0, 1)) / DAY_MS / DAYS_PER_WEEK) + 1;
  return { year, week };
}

/**
 * The Monday of an ISO week label, as `YYYY-MM-DD`, or null when the label is not one.
 *
 * Rejects a week the year does not have: only a year whose January 1st is a Thursday (or a
 * leap year starting on a Wednesday) has 53 weeks, and `2025-W53` would otherwise silently
 * resolve to the Monday of 2026-W01 -- a fact filed a year off rather than a reported
 * violation. The round trip is the whole guard: any week number the year does not have --
 * `W00` as much as `W54` -- lands its Monday in a neighbouring ISO year, which the check
 * catches. UTC throughout, so the answer does not depend on the renderer's timezone.
 */
export function isoWeekStart(label: string): string | null {
  const parsed = ISO_WEEK_LABEL.exec(label);
  if (!parsed) return null;

  const year = Number(parsed[1]);
  const week = Number(parsed[2]);
  const monday = mondayOfIsoWeek(year, week);
  const roundTrip = isoWeekOf(monday);
  if (roundTrip.year !== year || roundTrip.week !== week) return null;

  return new Date(monday).toISOString().slice(0, 10);
}
